import * as stylex from "@stylexjs/stylex";
import { useState, type ComponentProps } from "react";
import { useRouteContext, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpc, trpcClient } from "@/lib/trpc";
import { clearReportingView, mediaPerformancePath, resolveReportingView, type ReportingView } from "@/lib/reporting-view";
import { createReportSearchParams } from "@/lib/report-search-params";
import { relativeReportingPresets, workspaceDefaultSchema } from "@/lib/workspace-default";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Button from "@/components/ui/Button";
import Field from "@/components/ui/Field";
import "@/design/tokens.stylex";
import { colors, space, text } from "@/design/tokens.stylex";
import MediaPerformanceActionsMenu from "./MediaPerformanceActionsMenu";
export default function WorkspaceDefaultActions(actions: ComponentProps<typeof MediaPerformanceActionsMenu>) {
  const {
    reportingView
  } = useRouteContext({
    from: "/_authenticated/$orgSlug/media-performance"
  });
  const {
    workspace
  } = reportingView;
  const queryClient = useQueryClient();
  const router = useRouter();
  const [draft, setDraft] = useState<{
    version: number;
    preset: string;
    view: ReportingView;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const options = trpc.mediaPerformanceConfiguration.load.queryOptions({
    orgId: workspace.id
  });
  async function reset() {
    setBusy(true);
    const location = router.state.location.href;
    try {
      // Cancel any older read so an in-flight cached snapshot cannot win reset.
      await queryClient.cancelQueries({
        queryKey: options.queryKey
      });
      const configuration = await queryClient.fetchQuery({
        ...options,
        staleTime: 0
      });
      if (router.state.location.href !== location) return;
      const resolved = resolveReportingView({
        search: {},
        explicit: false,
        configuration
      });
      clearReportingView(reportingView.scope);
      await router.navigate({
        href: `${mediaPerformancePath(workspace.slug, resolved.search.view)}?${createReportSearchParams(resolved.search, new Set())}`,
        replace: true
      });
      toast.success("View reset to the Company default.");
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "Could not reset the view. Try again.");
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!draft || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const view = workspaceDefaultSchema.parse({
        ...draft.view,
        preset: draft.preset
      });
      await trpcClient.mediaPerformanceConfiguration.save.mutate({
        orgId: workspace.id,
        expectedVersion: draft.version,
        change: {
          type: "workspaceDefault",
          view
        }
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the Company default. Try again.");
      setBusy(false);
      return;
    }
    toast.success("View saved as the Company default. Teammates without their own view now open it.");
    // The mutation has committed. Cache refresh failure must not report a failed save.
    try {
      await queryClient.invalidateQueries({
        queryKey: options.queryKey
      });
      await router.invalidate();
    } catch {
      toast.error("Default saved, but the report could not be refreshed. Reload before saving again.");
    } finally {
      setDraft(null);
      setBusy(false);
    }
  }
  return <>
      <MediaPerformanceActionsMenu {...actions} defaultBusy={busy} onResetDefault={() => void reset()} onSaveDefault={workspace.role === "admin" && !reportingView.unavailable.length ? () => {
      setError(undefined);
      setDraft({
        version: reportingView.configuration.version,
        view: reportingView.search,
        preset: reportingView.search.preset === "custom" ? "" : reportingView.search.preset
      });
    } : undefined} />
      {draft && <Modal title="Save view as the Company default" size="md" onClose={() => {
      if (!busy) setDraft(null);
    }} footer={<>
              <Button variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy || !draft.preset} onClick={() => void save()}>
                {busy ? "Saving…" : "Save view"}
              </Button>
            </>}>
          <div className="x78zum5 xdt5ytf x1l51sop">
            <p className="x1ghz6dp xtu3avg x1evy7pa x1e3avs9">
              This replaces the single shared default for {workspace.name}. It affects teammates
              without a personal remembered view. Existing personal views will not be reset.
            </p>
            <Field label="Relative date preset">
              <Select value={draft.preset} onChange={event => setDraft({
            ...draft,
            preset: event.target.value
          })} disabled={busy}>
                <option value="" disabled>
                  Choose a relative preset
                </option>
                {Object.entries(relativeReportingPresets).map(([value, label]) => <option key={value} value={value}>
                    {label}
                  </option>)}
              </Select>
            </Field>
            <p className="x1ghz6dp x7x6yu3 x1i6qc9y">
              Custom dates cannot be a default. Your report keeps its current dates.
            </p>
            {error && <p role="alert" className="x1ghz6dp x7x6yu3 x1nif09i">
                {error}
              </p>}
          </div>
        </Modal>}
    </>;
}